import type { ContactRow } from '@shared/flow/contact-view';
import { hasPhone } from './hasPhone';
import { EMPTY_DIALABLE, type DialableSelection, type SkippedDialCounts } from './dialableSignals';

/** The row fields the contact dialable rule reads (so partial fixtures and the table's `ContactRow` both fit). */
export type DialableContactInput = Pick<ContactRow, 'id'> & Partial<Pick<ContactRow, 'best_phone' | 'do_not_call'>>;

/**
 * THE dialable-subset rule for a contact list (People, a Plays section, the account Contacts card): Trellus rejects a
 * selection that mixes dialable and non-dialable contacts (`mixed_selection`), so Terra publishes only the checked
 * contacts that have a phone and are not do-not-call. The native checkboxes keep every checked row, so Enroll still
 * acts on all of them. First matching reason wins (so the counts add up to the skipped total); contacts are never
 * `notCall` or `other` (those are Signals task concepts).
 */
export function partitionDialableContacts(rows: readonly DialableContactInput[], selectedIds: ReadonlySet<string>): DialableSelection {
  if (selectedIds.size === 0) return EMPTY_DIALABLE;
  const dialableIds: string[] = [];
  const skipped: SkippedDialCounts = { notCall: 0, dnc: 0, noPhone: 0, finding: 0, other: 0 };
  const enrichableContactIds: string[] = [];
  const seen = new Set<string>();
  let selectedCount = 0;
  for (const row of rows) {
    // Overlapping pages can repeat a contact (its sort value moved between fetches); Trellus rejects duplicate ids.
    if (!selectedIds.has(row.id) || seen.has(row.id)) continue;
    seen.add(row.id);
    selectedCount += 1;
    if (row.do_not_call === true) skipped.dnc += 1;
    else if (!hasPhone(row.best_phone)) {
      skipped.noPhone += 1;
      enrichableContactIds.push(row.id);
    }
    else dialableIds.push(row.id);
  }
  return { dialableIds, enrichableContactIds, skipped, selectedCount };
}
